/**
 * Magnus Context Builder
 * 
 * Builds comprehensive context for Magnus Basic and Prime modes
 * providing app awareness, data summaries, and navigation context
 */

import type { DfrData } from '../types';
import { Location } from 'react-router-dom';
import { isItemInProject } from './assistantHelpers';

// Page context mapping (same as in MagnusAssistant)
export const PAGE_CONTEXT: Record<string, {
    name: string;
    purpose: string;
    category: string;
    nextSteps: string[];
}> = {
    '/dashboard': {
        name: 'Dashboard',
        category: 'Core',
        purpose: 'Overview of your project health, RPN trends, and quick access to FMEAs',
        nextSteps: ['Create new FMEAs', 'Review high-RPN items', 'Check materials database']
    },
    '/fmeas': {
        name: 'FMEA Management',
        category: 'Risk Analysis',
        purpose: 'View and manage all your Failure Modes and Effects Analysis',
        nextSteps: ['Create new FMEA', 'Edit existing FMEA', 'Export FMEAs']
    },
    '/fmea-editor': {
        name: 'FMEA Editor',
        category: 'Risk Analysis',
        purpose: 'Create and edit individual Failure Modes with severity, occurrence, and detection scores',
        nextSteps: ['Add failure mode', 'Calculate RPN', 'Add controls', 'Link to DVP&R tests']
    },
    '/risk-analysis': {
        name: 'Risk Analysis',
        category: 'Risk Analysis',
        purpose: 'Deep dive into RPN trends, risk matrices, and mitigation tracking',
        nextSteps: ['Review high RPNs', 'Check S×O vs S×D matrix', 'Track mitigation progress']
    },
    '/p-diagram': {
        name: 'P-Diagram',
        category: 'System Design',
        purpose: 'View Parameter Diagrams showing system context, noise factors, and ideal functions',
        nextSteps: ['Create P-Diagram', 'Link to FMEAs', 'Identify noise factors']
    },
    '/fmea-ideation': {
        name: 'FMEA Ideation',
        category: 'AI Tools',
        purpose: 'AI-powered FMEA generation from P-Diagrams and customer requirements',
        nextSteps: ['Select P-Diagram', 'Generate FMEA drafts', 'Review and approve']
    },
    '/pbs-manager': {
        name: 'PBS Manager',
        category: 'Product Structure',
        purpose: 'Manage Product Breakdown Structure showing component hierarchy',
        nextSteps: ['Add components', 'Define hierarchy', 'Link to FMEAs']
    },
    '/bom-functions': {
        name: 'BOM Functions',
        category: 'Product Structure',
        purpose: 'Define the basic functions for each Bill of Materials component',
        nextSteps: ['Define functions', 'Link to requirements', 'Create FMEAs for functions']
    },
    '/dvpr': {
        name: 'DVP&R',
        category: 'Testing & Validation',
        purpose: 'Design Verification Plan & Report - test specifications linked to FMEAs',
        nextSteps: ['Create test specs', 'Link to FMEAs', 'Track test results']
    },
    '/reliability-plan': {
        name: 'Reliability Test Plan',
        category: 'Testing & Validation',
        purpose: 'LIMS and verification suite for reliability testing',
        nextSteps: ['View test specifications', 'Track results', 'Analyze trends']
    },
    '/materials-database': {
        name: 'Materials Database',
        category: 'Product Data',
        purpose: 'Material specifications, validation data, and supplier information',
        nextSteps: ['Add materials', 'Validate specs', 'Link to components']
    },
    '/graph-view': {
        name: 'Knowledge Graph',
        category: 'Visualization',
        purpose: 'Interactive graph showing relationships between functions, FMEAs, controls, and tests',
        nextSteps: ['Explore connections', 'Find weak links', 'Identify missing links']
    },
    '/traceability-matrix': {
        name: 'Traceability Matrix',
        category: 'Documentation',
        purpose: 'Complete traceability from requirements through FMEAs to tests',
        nextSteps: ['Check completeness', 'Find gaps', 'Export for audits']
    },
    '/customer-requirements': {
        name: 'Customer Requirements',
        category: 'Planning',
        purpose: 'Track customer requirements and link them to product features and tests',
        nextSteps: ['Add requirements', 'Link to features', 'Create FMEAs']
    },
    '/ecn-management': {
        name: 'ECN Management',
        category: 'Change Management',
        purpose: 'Engineering Change Notices tracking design iterations and RPN improvements',
        nextSteps: ['Create ECN', 'Track RPN changes', 'Document iterations']
    }
};

export interface MagnusContext {
    // Page Context
    currentPage: string;
    pageName: string;
    pageCategory: string;
    pagePurpose: string;
    nextSteps: string[];

    // Project Context
    activeProject: string;
    projectDisplayName: string;
    fmeaStandard?: string;

    // Data Summary
    dataSummary: {
        totalFMEAs: number;
        activeFMEAs: number;
        draftFMEAs: number;
        highRiskCount: number;
        criticalRiskCount: number;
        safetyRiskCount: number;
        avgRPN: number;

        totalBOMItems: number;
        totalControls: number;
        totalTests: number;
        totalRequirements: number;
        totalIterations: number;
        totalECNs: number;

        // Coverage metrics
        fmeaCoverage: number;  // % of components with FMEAs
        testCoverage: number;  // % of FMEAs with linked tests
        requirementCoverage: number;  // % of requirements with FMEAs
    };

    // Current View State
    viewState?: {
        isDraftMode?: boolean;
        selectedFunction?: string;
        filterCriteria?: any;
    };

    // Navigation
    availablePages: Array<{
        path: string;
        name: string;
        category: string;
    }>;
}

/**
 * Build comprehensive context for Magnus
 */
const getProjectFMEAs = (allData: DfrData, activeProject: string) => {
    const allFMEAs = allData?.fmeaItems || [];
    return allFMEAs.filter(f => isItemInProject(activeProject, allData, f.projectName, f.epochId));
};

const generateDataSummary = (
    projectFMEAs: any[],
    activeFMEAs: any[],
    draftFMEAs: any[],
    projectBOM: any[],
    allData: DfrData,
    riskMetrics: any,
    coverageMetrics: any
) => {
    const controls = allData?.controls || [];
    const iterations = allData?.iterationHistory || [];
    const ecns = allData?.ecnLog || [];

    return {
        totalFMEAs: projectFMEAs.length,
        activeFMEAs: activeFMEAs.length,
        draftFMEAs: draftFMEAs.length,

        highRiskCount: riskMetrics.highRiskCount,
        criticalRiskCount: riskMetrics.criticalRiskCount,
        safetyRiskCount: riskMetrics.safetyRiskCount,
        avgRPN: riskMetrics.avgRPN,

        totalBOMItems: projectBOM.length,
        totalControls: controls.length,
        totalTests: coverageMetrics.totalTests,
        totalRequirements: coverageMetrics.totalRequirements,
        totalIterations: iterations.length,
        totalECNs: ecns.length,

        fmeaCoverage: coverageMetrics.fmeaCoverage,
        testCoverage: coverageMetrics.testCoverage,
        requirementCoverage: coverageMetrics.requirementCoverage
    };
};

const getViewState = (location: Location) => {
    const viewState = location.state as any;
    return {
        isDraftMode: viewState?.view === 'draft' || window.location.href.includes('draft'),
        selectedFunction: viewState?.selectedFunction,
        filterCriteria: viewState?.filter
    };
};

const getProjectBOM = (allData: DfrData, activeProject: string) => {
    const bomItems = allData?.bomItems || [];
    return bomItems.filter(b => isItemInProject(activeProject, allData, b.projectName, b.epochId));
};

const calculateRiskMetrics = (activeFMEAs: any[]) => {
    return {
        highRiskCount: activeFMEAs.filter(f => (f.S * f.O * f.D) > 100).length,
        criticalRiskCount: activeFMEAs.filter(f => (f.S * f.O * f.D) > 200).length,
        safetyRiskCount: activeFMEAs.filter(f => f.S >= 8).length,
        avgRPN: activeFMEAs.length > 0
            ? activeFMEAs.reduce((sum: number, f: any) => sum + (f.S * f.O * f.D), 0) / activeFMEAs.length
            : 0
    };
};

const calculateCoverageMetrics = (projectFMEAs: any[], projectBOM: any[], allData: DfrData) => {
    const tests = allData?.dvprTests || [];
    const requirements = allData?.customerRequirements || [];

    const bomWithFMEAs = projectBOM.filter(b =>
        projectFMEAs.some(f => f.linkedBomIds?.includes(b.id))
    ).length;

    const fmeasWithTests = projectFMEAs.filter(f =>
        tests.some(t => t.traceableRiskId?.includes(f.id))
    ).length;

    const reqsWithFMEAs = requirements.filter(r =>
        projectFMEAs.some(f => f.linkedCustomerReqIds?.includes(r.id))
    ).length;

    return {
        fmeaCoverage: projectBOM.length > 0 ? (bomWithFMEAs / projectBOM.length) * 100 : 0,
        testCoverage: projectFMEAs.length > 0 ? (fmeasWithTests / projectFMEAs.length) * 100 : 0,
        requirementCoverage: requirements.length > 0 ? (reqsWithFMEAs / requirements.length) * 100 : 0,
        totalTests: tests.length,
        totalRequirements: requirements.length
    };
};

/**
 * Build comprehensive context for Magnus
 */
export const buildMagnusContext = (
    location: Location,
    allData: DfrData,
    activeProject: string
): MagnusContext => {
    const currentPath = location.pathname;
    const pageContext = PAGE_CONTEXT[currentPath];

    const projectFMEAs = getProjectFMEAs(allData, activeProject);
    const activeFMEAs = projectFMEAs.filter(f => (f.status || 'active') === 'active');
    const draftFMEAs = projectFMEAs.filter(f => (f.status || 'draft') === 'draft');

    const projectBOM = getProjectBOM(allData, activeProject);
    const riskMetrics = calculateRiskMetrics(activeFMEAs);
    const coverageMetrics = calculateCoverageMetrics(projectFMEAs, projectBOM, allData);

    return {
        // Page Context
        currentPage: currentPath,
        pageName: pageContext?.name || 'Unknown Page',
        pageCategory: pageContext?.category || 'Other',
        pagePurpose: pageContext?.purpose || 'Navigate the reliability engineering system',
        nextSteps: pageContext?.nextSteps || [],

        // Project Context
        activeProject,
        projectDisplayName: activeProject === 'all' ? 'All Projects' : activeProject,
        fmeaStandard: allData.projectMetadata?.fmeaStandard,

        // Data Summary
        dataSummary: generateDataSummary(
            projectFMEAs, activeFMEAs, draftFMEAs, projectBOM, allData, riskMetrics, coverageMetrics
        ),

        // View State
        viewState: getViewState(location),

        // Navigation
        availablePages: Object.entries(PAGE_CONTEXT).map(([path, info]) => ({
            path,
            name: info.name,
            category: info.category
        }))
    };
};

/**
 * Build a concise summary string for AI prompts
 */
export const buildContextSummary = (context: MagnusContext): string => {
    const { dataSummary } = context;

    let summary = `CURRENT CONTEXT:\n`;
    summary += `• Page: ${context.pageName} (${context.pageCategory})\n`;
    summary += `• Purpose: ${context.pagePurpose}\n`;
    summary += `• Project: ${context.projectDisplayName}`;
    if (context.fmeaStandard) {
        summary += ` (${context.fmeaStandard} standard)`;
    }
    summary += `\n\n`;

    summary += `DATA SUMMARY:\n`;
    summary += `• ${dataSummary.totalFMEAs} FMEAs (${dataSummary.activeFMEAs} active, ${dataSummary.draftFMEAs} draft)\n`;

    if (dataSummary.highRiskCount > 0) {
        summary += `• ⚠️ ${dataSummary.highRiskCount} high-risk items (RPN > 100)\n`;
    }
    if (dataSummary.safetyRiskCount > 0) {
        summary += `• 🚨 ${dataSummary.safetyRiskCount} safety-critical items (S ≥ 8)\n`;
    }

    summary += `• ${dataSummary.totalBOMItems} components, ${dataSummary.totalTests} tests\n`;
    summary += `• Average RPN: ${dataSummary.avgRPN.toFixed(1)}\n`;

    if (dataSummary.testCoverage < 50) {
        summary += `• ⚠️ Test coverage: ${dataSummary.testCoverage.toFixed(0)}% (needs improvement)\n`;
    }

    if (context.viewState?.isDraftMode) {
        summary += `\n📝 USER IS IN DRAFT REVIEW MODE\n`;
    }

    return summary;
};
